import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { useTheme } from '@/hooks/useTheme'
import { Button } from '@/components/ui/button'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import { ACTIVE_CHAINS, chainName } from '@/config/chains'
import { getContractAddress } from '@/config/contracts'
import { ENV } from '@/config/env'

export const Route = createFileRoute('/settings')({
    component: SettingsPage,
})

function SettingsPage() {
    const { theme, setTheme } = useTheme()
    const { address, rolesByChain } = useOperatorSession()

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Settings</h1>
                <p className="text-muted-foreground text-sm">
                    Identity, environment, and console preferences.
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Identity</CardTitle>
                        <CardDescription>Wallet currently signed in.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <AddressDisplay address={address} truncate={false} />
                        <div className="space-y-1.5">
                            {ACTIVE_CHAINS.map((c) => (
                                <div
                                    key={c.id}
                                    className="border-border flex items-center justify-between rounded border px-3 py-1.5 text-sm"
                                >
                                    <span>{chainName(c.id)}</span>
                                    {rolesByChain[c.id] ? (
                                        <Badge variant="positive">on-chain OPERATOR_ROLE</Badge>
                                    ) : (
                                        <Badge variant="muted">service-signed writes</Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className="text-muted-foreground text-[11px]">
                            On-chain OPERATOR_ROLE belongs to the gaming service's signer, which
                            executes chain writes through the operations queue — console actions
                            never need it on your wallet.
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Theme</CardTitle>
                        <CardDescription>
                            Light by default; persisted to localStorage.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex gap-2">
                        <Button
                            variant={theme === 'light' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setTheme('light')}
                        >
                            Light
                        </Button>
                        <Button
                            variant={theme === 'dark' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setTheme('dark')}
                        >
                            Dark
                        </Button>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Environment</CardTitle>
                    <CardDescription>
                        Read-only. Edit via <code className="font-mono">.env</code> and restart.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <Row label="App mode">{ENV.APP_MODE}</Row>
                    <Row label="Gaming service URL">
                        <code className="font-mono text-xs">{ENV.GAMING_SERVICE_URL}</code>
                    </Row>
                    <Row label="WalletConnect">
                        {ENV.WALLETCONNECT_PROJECT_ID ? (
                            'configured'
                        ) : (
                            <Badge variant="muted">unset (injected only)</Badge>
                        )}
                    </Row>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Configured chains</CardTitle>
                    <CardDescription>Per-chain contract addresses.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {ACTIVE_CHAINS.map((c) => (
                            <div key={c.id} className="border-border rounded-md border p-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">{chainName(c.id)}</span>
                                    <span className="text-muted-foreground text-xs">id {c.id}</span>
                                </div>
                                <div className="mt-2 grid gap-1 text-xs">
                                    <ContractRow
                                        label="PlayerRegistry"
                                        address={getContractAddress(c.id, 'playerRegistry')}
                                        chainId={c.id}
                                    />
                                    <ContractRow
                                        label="KYCRegistry"
                                        address={getContractAddress(c.id, 'kycRegistry')}
                                        chainId={c.id}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{label}</span>
            <span>{children}</span>
        </div>
    )
}

function ContractRow({
    label,
    address,
    chainId,
}: {
    label: string
    address?: string
    chainId: number
}) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{label}</span>
            {address ? (
                <AddressDisplay address={address} chainId={chainId} />
            ) : (
                <Badge variant="muted">unset</Badge>
            )}
        </div>
    )
}
